import { create } from "zustand";
import { logEvent } from "./useLogsStore.jsx";
import { compactHeartRateRecoverySamples, retainHeartRateSessionSamples } from "../utils/heartRateSession.js";

const MAX_SESSION_SAMPLES = 60 * 60 * 8;
// The live Zustand state keeps the complete session. The recovery copy must be
// small enough to coexist with maps/history in WebView localStorage.
const MAX_PERSISTED_SESSION_SAMPLES = 10000;
const HEART_RATE_SESSION_KEY = "trekkin.heartRate.activeSession.v1";
let lastSessionPersistAt = 0;
let lastSessionPersistErrorAt = 0;

const readPersistedSession = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(HEART_RATE_SESSION_KEY) || "null");
    const samples = Array.isArray(parsed?.samples)
      ? parsed.samples.map((sample) => Array.isArray(sample)
        ? { ts: Number(sample[0]), bpm: Number(sample[1]), source: parsed?.source || "unknown", deviceName: parsed?.deviceName || null, sparse: true }
        : { ...sample, sparse: true })
      : [];
    return {
      samples: samples.slice(-MAX_SESSION_SAMPLES),
      source: parsed?.source || null,
      deviceName: parsed?.deviceName || null,
      lastSampleAt: Number(parsed?.lastSampleAt) || null,
    };
  } catch {
    return { samples: [], source: null, deviceName: null, lastSampleAt: null };
  }
};

const persistSession = (state) => {
  try {
    const now = Date.now();
    if (lastSessionPersistAt && now - lastSessionPersistAt < 5000) return;
    lastSessionPersistAt = now;
    const sourceSamples = Array.isArray(state.samples) ? state.samples : [];
    const persistedSamples = compactHeartRateRecoverySamples(
      sourceSamples,
      MAX_PERSISTED_SESSION_SAMPLES
    );
    localStorage.setItem(HEART_RATE_SESSION_KEY, JSON.stringify({
      samples: persistedSamples.map((sample) => [sample.ts, sample.bpm]),
      source: state.source,
      deviceName: state.deviceName,
      lastSampleAt: state.lastSampleAt,
    }));
  } catch (error) {
    const now = Date.now();
    if (!lastSessionPersistErrorAt || now - lastSessionPersistErrorAt >= 30000) {
      lastSessionPersistErrorAt = now;
      logEvent("warn", "HEART_RATE_SESSION_PERSIST_FAILED", {
        sampleCount: state?.samples?.length || 0,
        error: error?.message || String(error),
      });
    }
  }
};

const recoveredSession = readPersistedSession();
const emptyDiagnostics = () => ({
  received: 0,
  accepted: 0,
  invalid: 0,
  duplicate: 0,
  sourceMismatch: 0,
  firstReceivedAt: null,
  firstAcceptedAt: null,
  lastReceivedAt: null,
  lastRejectedSource: null,
});

export const useHeartRateStore = create((set, get) => ({
  preferredSource: (() => {
    try { return localStorage.getItem("trekkin.heartRate.source") || "auto"; }
    catch { return "auto"; }
  })(),
  supported: false,
  permission: "unknown",
  transport: null,
  status: "idle",
  bpm: null,
  source: recoveredSession.source,
  deviceName: recoveredSession.deviceName,
  lastSampleAt: recoveredSession.lastSampleAt,
  samples: recoveredSession.samples,
  autoLockedSource: recoveredSession.source,
  error: null,
  diagnostics: emptyDiagnostics(),

  getDiagnostics: () => ({ ...get().diagnostics }),

  setPreferredSource: (preferredSource) => {
    const value = String(preferredSource || "auto");
    try { localStorage.setItem("trekkin.heartRate.source", value); } catch {}
    set({ preferredSource: value });
  },

  setCapability: (payload = {}) =>
    set({
      supported: !!payload.supported,
      permission: payload.permission || get().permission,
      transport: payload.transport || get().transport,
      status: payload.status || get().status,
      deviceName: payload.deviceName || get().deviceName,
      error: payload.error || null,
    }),

  beginSession: ({ preserveSamples = false } = {}) =>
    set((state) => ({
      status: "connecting",
      bpm: preserveSamples && state.samples.length ? state.samples[state.samples.length - 1].bpm : null,
      source: preserveSamples ? state.source : null,
      deviceName: preserveSamples ? state.deviceName : null,
      lastSampleAt: preserveSamples ? state.lastSampleAt : null,
      samples: preserveSamples ? state.samples : [],
      autoLockedSource: preserveSamples ? state.autoLockedSource : null,
      diagnostics: preserveSamples ? state.diagnostics : emptyDiagnostics(),
      error: null,
    })),

  addSample: (payload = {}) => {
    const bpm = Math.round(Number(payload.bpm));
    const ts = Number(payload.ts) || Date.now();
    const source = payload.source || "unknown";
    set((state) => ({
      diagnostics: {
        ...state.diagnostics,
        received: state.diagnostics.received + 1,
        firstReceivedAt: state.diagnostics.firstReceivedAt || ts,
        lastReceivedAt: ts,
      },
    }));
    if (!Number.isFinite(bpm) || bpm < 25 || bpm > 260) {
      set((state) => ({
        diagnostics: { ...state.diagnostics, invalid: state.diagnostics.invalid + 1 },
      }));
      return;
    }
    const sample = {
      bpm,
      ts,
      source,
      deviceName: payload.deviceName || null,
    };
    if (get().samples.some((existing) => existing.ts === ts && existing.source === sample.source)) {
      set((state) => ({
        diagnostics: { ...state.diagnostics, duplicate: state.diagnostics.duplicate + 1 },
      }));
      return;
    }
    const preferred = get().preferredSource;
    const haystack = `${sample.source} ${sample.deviceName || ""}`.toLowerCase();
    const autoLockedSource = get().autoLockedSource;
    const matches =
      (preferred === "auto" && (!autoLockedSource || autoLockedSource === sample.source)) ||
      (preferred === "airpods" && haystack.includes("airpod")) ||
      (preferred === "apple-watch" && haystack.includes("watch") && !haystack.includes("garmin")) ||
      (preferred === "garmin-band" && (sample.source === "garmin-band" || haystack.includes("hrm") || haystack.includes("heart rate"))) ||
      (preferred === "garmin-watch" && /garmin|fenix|fēnix|forerunner|epix|instinct|venu/.test(haystack)) ||
      (preferred === "wear-os" && (sample.source === "wear-os" || /wear|pixel watch|galaxy watch/.test(haystack))) ||
      (preferred === "bluetooth" && sample.source === "bluetooth");
    if (!matches) {
      set((state) => ({
        diagnostics: {
          ...state.diagnostics,
          sourceMismatch: state.diagnostics.sourceMismatch + 1,
          lastRejectedSource: sample.source,
        },
      }));
      return;
    }
    set((state) => {
      const accepted = state.diagnostics.accepted + 1;
      const next = {
        supported: true,
        status: "active",
        bpm,
        source: sample.source,
        deviceName: sample.deviceName,
        lastSampleAt: ts,
        autoLockedSource: preferred === "auto" ? (state.autoLockedSource || sample.source) : null,
        error: null,
        samples: retainHeartRateSessionSamples([...state.samples, sample], MAX_SESSION_SAMPLES),
        diagnostics: {
          ...state.diagnostics,
          accepted,
          firstAcceptedAt: state.diagnostics.firstAcceptedAt || ts,
        },
      };
      persistSession(next);
      if (accepted === 1 || accepted === 10 || accepted % 300 === 0) {
        logEvent("info", "HEART_RATE_SAMPLE_CHECKPOINT", {
          accepted,
          received: next.diagnostics.received,
          source: sample.source,
          preferredSource: state.preferredSource,
          bpm,
        });
      }
      return next;
    });
  },

  mergeNativeSamples: (payloads = []) => {
    if (!Array.isArray(payloads) || !payloads.length) return 0;
    const state = get();
    const samplesByKey = new Map(
      (state.samples || []).map((sample) => [
        `${Number(sample?.ts) || 0}:${sample?.source || "unknown"}`,
        sample,
      ])
    );
    const recovered = [];
    let replacedSparse = 0;
    for (const payload of payloads) {
      const bpm = Math.round(Number(payload?.bpm));
      const ts = Number(payload?.ts || payload?.time || payload?.timestamp);
      const source = payload?.source || "bluetooth";
      if (!Number.isFinite(bpm) || bpm < 25 || bpm > 260 || !Number.isFinite(ts) || ts <= 0) continue;
      const key = `${ts}:${source}`;
      const nativeSample = { bpm, ts, source, deviceName: payload?.deviceName || null };
      const existing = samplesByKey.get(key);
      if (existing) {
        // A sessão recuperada do localStorage é compactada e marcada como
        // sparse. Quando chega o snapshot nativo, substitui essa cópia pela
        // amostra autoritativa para que futuras retomas possam voltar a usar
        // sincronização incremental.
        if (existing.sparse === true) {
          samplesByKey.set(key, nativeSample);
          replacedSparse += 1;
        }
        continue;
      }
      samplesByKey.set(key, nativeSample);
      recovered.push(nativeSample);
    }
    if (!recovered.length && !replacedSparse) return 0;
    recovered.sort((a, b) => a.ts - b.ts);
    set((current) => {
      const samples = Array.from(samplesByKey.values())
        .sort((a, b) => Number(a.ts) - Number(b.ts));
      const retainedSamples = retainHeartRateSessionSamples(samples, MAX_SESSION_SAMPLES);
      const last = retainedSamples[retainedSamples.length - 1];
      const next = {
        supported: true,
        status: current.status === "paused" ? "paused" : "active",
        bpm: last?.bpm || current.bpm,
        source: last?.source || current.source,
        deviceName: last?.deviceName || current.deviceName,
        lastSampleAt: last?.ts || current.lastSampleAt,
        samples: retainedSamples,
        diagnostics: {
          ...current.diagnostics,
          received: current.diagnostics.received + recovered.length,
          accepted: current.diagnostics.accepted + recovered.length,
          firstReceivedAt: current.diagnostics.firstReceivedAt || recovered[0].ts,
          firstAcceptedAt: current.diagnostics.firstAcceptedAt || recovered[0].ts,
          lastReceivedAt: last?.ts || current.diagnostics.lastReceivedAt,
        },
      };
      persistSession(next);
      return next;
    });
    return recovered.length + replacedSparse;
  },

  setStatus: (status, error = null) => set({ status, error }),

  finishSession: () => {
    const samples = get().samples;
    const values = samples.map((sample) => sample.bpm).filter(Number.isFinite);
    const summary = values.length
      ? {
          sampleCount: values.length,
          averageBpm: Math.round(values.reduce((sum, value) => sum + value, 0) / values.length),
          minBpm: Math.min(...values),
          maxBpm: Math.max(...values),
          source: get().source,
          deviceName: get().deviceName,
          samples: [...samples],
        }
      : null;
    logEvent(summary ? "info" : "warn", "HEART_RATE_SESSION_FINISH", {
      sampleCount: summary?.sampleCount || 0,
      averageBpm: summary?.averageBpm || null,
      minBpm: summary?.minBpm || null,
      maxBpm: summary?.maxBpm || null,
      source: get().source || null,
      preferredSource: get().preferredSource,
      status: get().status,
      diagnostics: get().diagnostics,
    });
    try { localStorage.removeItem(HEART_RATE_SESSION_KEY); } catch {}
    lastSessionPersistAt = 0;
    lastSessionPersistErrorAt = 0;
    set({ status: "idle", bpm: null, lastSampleAt: null, autoLockedSource: null });
    return summary;
  },
}));
